import * as easings from 'd3-ease';
import React, {FC, useCallback} from 'react';
import {animated, useTransition} from 'react-spring';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../../../libs/shared/ui/src/styles/paletteModel';
import {boxShadow} from '../../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {
  InterfaceEventsEnum,
  RecentCommunicationsBannerActionsEnum,
  RecentCommunicationsBannerLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../services/tracking';
import {RecentCommunicationsBannerStatusesEnum} from '../../../../../../store/metadata/recentCommunications/recentCommunicationsState';
import {BannerCloseAction} from '../../../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../../../common/banners/bannerContent';
import {ComposerInfoBanner} from '../../../../composer/banner/composerInfoBanner';
import {ComposerInfoBannerAction} from '../../../../composer/banner/composerInfoBannerAction';
import {ComposerInfoBannerSubtitle} from '../../../../composer/banner/composerInfoBannerSubtitle';
import {StaticViewerBanner} from '../../../activity/static/staticViewerBanner';
import {StaticViewerBannerAction} from '../../../activity/static/staticViewerBannerAction';

/*
 * Types.
 */

interface RecentCommunicationsBannerProps {
  onClose: Handler;
  onView: Handler;
  visualRecipient: VisualRecipientModel;
  recentCommunicationsBannerStatus: RecentCommunicationsBannerStatusesEnum;
  recentCommunicationsCount: number;
  shouldRenderAsComposerBanner: boolean;
}

/*
 * Style.
 */

const StyledStaticViewerBanner = styled(StaticViewerBanner)`
  ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
  padding: 4px 12px;
`;

const StyledBannerContent = styled(BannerContent)`
  align-items: center;
  color: ${(p) => p.theme.greys.shade80};
  display: flex;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  padding: 0px; /* Overrides the padding in BannerContent */
  margin-bottom: 0;
`;

const StyledStaticViewerBannerAction = styled(StaticViewerBannerAction)`
  color: ${(p) => p.theme.palette.teal.shade50};
  margin: 0px 0px 0px 10px;
  min-width: max-content;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledBannerCloseAction = styled(BannerCloseAction)`
  color: ${(p) => p.theme.greys.shade60};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledDisplayNameDiv = styled.div`
  ${ellipsis()};
`;

const StyledComposerInfoBannerAction = styled(ComposerInfoBannerAction)`
  color: ${(p) => p.theme.palette.teal.shade50};
  min-width: max-content;
`;

/*
 * Component.
 */

export const RecentCommunicationsBanner: FC<RecentCommunicationsBannerProps> = (props) => {
  const {
    recentCommunicationsBannerStatus,
    recentCommunicationsCount,
    onClose,
    onView,
    shouldRenderAsComposerBanner,
    visualRecipient,
  } = props;

  // If the banner's status changes, we want to update the child in order to trigger the leave animation
  // Returning null here would unmount the component entirely, which precludes the animation from running
  const shouldShowBanner =
    recentCommunicationsBannerStatus === RecentCommunicationsBannerStatusesEnum.VISIBLE;
  const animationHeight = shouldRenderAsComposerBanner ? 'auto' : 50;
  const transitions = useTransition(shouldShowBanner, {
    from: {opacity: 0, height: 0, transform: 'translateY(-16px)'},
    enter: {opacity: 1, height: animationHeight, transform: 'translateY(0px)'},
    leave: {opacity: 0, height: 0, transform: 'translateY(-16px)'},
    config: {duration: 300, easing: easings.easeCubic},
  });

  const locationForTracking = shouldRenderAsComposerBanner
    ? RecentCommunicationsBannerLocationsEnum.WITHIN_COMPOSER
    : RecentCommunicationsBannerLocationsEnum.ABOVE_COMPOSER;

  useInitialEffect(() => {
    if (shouldShowBanner) {
      trackInterfaceEvent(InterfaceEventsEnum.P3_RECENT_CONVERSATIONS_BANNER_VIEW, {
        namespace: visualRecipient.namespace,
        num_conversations: recentCommunicationsCount,
        location: locationForTracking,
      });
    }
  });

  const onViewClick = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_RECENT_CONVERSATIONS_BANNER_CLICK, {
      namespace: visualRecipient.namespace,
      action: RecentCommunicationsBannerActionsEnum.VIEW,
      num_conversations: recentCommunicationsCount,
      location: locationForTracking,
    });
    onView();
  }, [onView, recentCommunicationsCount, visualRecipient, locationForTracking]);

  const onCloseClick = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_RECENT_CONVERSATIONS_BANNER_CLICK, {
      namespace: visualRecipient.namespace,
      action: RecentCommunicationsBannerActionsEnum.CLOSE,
      num_conversations: recentCommunicationsCount,
      location: locationForTracking,
    });
    onClose();
  }, [onClose, recentCommunicationsCount, visualRecipient, locationForTracking]);

  return (
    <>
      {transitions(
        (style, item) =>
          item && (
            <animated.div style={style}>{renderBannerContent(props, onViewClick, onCloseClick)}</animated.div>
          ),
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderBannerContent(
  props: RecentCommunicationsBannerProps,
  onViewClick: Handler,
  onCloseClick: Handler,
) {
  const {visualRecipient, recentCommunicationsCount, shouldRenderAsComposerBanner} = props;
  const displayName = visualRecipient.name || visualRecipient.contact.handle;

  // Render inside the composer
  if (shouldRenderAsComposerBanner) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ComposerInfoBanner
            color={PaletteColorsEnum.TEAL}
            onDismiss={onCloseClick}
            iconName="contacts"
            iconColor={theme.palette.teal.shade50}
          >
            <ComposerInfoBannerSubtitle>
              <span>
                <Plural
                  id="recent-communications-banner-text"
                  value={recentCommunicationsCount}
                  one={
                    <>
                      <strong>{recentCommunicationsCount} other conversation</strong>&nbsp;with&nbsp;
                      {displayName}&nbsp;in the last 7 days.
                    </>
                  }
                  other={
                    <>
                      <strong>{recentCommunicationsCount} other conversations</strong>&nbsp;with&nbsp;
                      {displayName}&nbsp;in the last 7 days.
                    </>
                  }
                />
              </span>
            </ComposerInfoBannerSubtitle>
            <StyledComposerInfoBannerAction onClick={onViewClick}>
              <Trans id="view-recent-communications-action">View</Trans>
            </StyledComposerInfoBannerAction>
          </ComposerInfoBanner>
        )}
      </UserThemeConsumer>
    );
  }

  // Render in the conversation timeline
  return (
    <StyledStaticViewerBanner color={PaletteColorsEnum.TEAL} iconName="contacts">
      <StyledBannerContent>
        <Plural
          id="recent-communications-banner-text"
          value={recentCommunicationsCount}
          one={
            <>
              <strong>{recentCommunicationsCount} other conversation</strong>&nbsp;with&nbsp;
              <StyledDisplayNameDiv>{displayName}</StyledDisplayNameDiv>&nbsp;in the last 7 days
            </>
          }
          other={
            <>
              <strong>{recentCommunicationsCount} other conversations</strong>&nbsp;with&nbsp;
              <StyledDisplayNameDiv>{displayName}</StyledDisplayNameDiv>&nbsp;in the last 7 days
            </>
          }
        />
        <StyledStaticViewerBannerAction onClick={onViewClick}>
          <Trans id="view-recent-communications-action">View</Trans>
        </StyledStaticViewerBannerAction>
        <StyledBannerCloseAction onClick={onCloseClick} />
      </StyledBannerContent>
    </StyledStaticViewerBanner>
  );
}
